/**
 * Детали финансового блока: карточки показателей, блоки с состояниями,
 * переключатели, сигналы. Каждый блок рисует все свои состояния сам:
 * загрузка той же геометрии, пусто с причиной, закрыто правами, ошибка.
 */

import { useState, type CSSProperties, type ReactNode } from 'react'
import { Icon, type IconName } from '../components/Icon'
import { KpiSkeleton, Notice } from '../components/ui'

export { KpiSkeleton }
import { Sparkline, TweenText } from './charts'
import { useDelayedFlag } from './motion'
import type { ReportState } from './data'
import { formatDate, formatDelta, formatDeltaPoints, formatRelative, formatDateTime } from '../format'

// --- Изменение к прошлому периоду ------------------------------------------

export function Delta({
  value,
  points,
  invert,
  label,
}: {
  value: number | null
  points?: boolean
  invert?: boolean
  label?: string
}) {
  if (value === null) return label ? <span className="muted">{label}</span> : null
  const good = invert ? value < 0 : value > 0
  const tone = value === 0 ? 'delta--flat' : good ? 'delta--up' : 'delta--down'
  return (
    <span className="kpi__delta">
      <span className={`delta ${tone}`}>
        <Icon name={value > 0 ? 'tri-up' : value < 0 ? 'tri-down' : 'dot'} size={10} />
        {points ? formatDeltaPoints(value) : formatDelta(value)}
      </span>
      {label && <span className="muted">{label}</span>}
    </span>
  )
}

// --- Карточка показателя ----------------------------------------------------

/** Короткая причина закрытия для карточки: полный текст сервера — в блоке ниже. */
export function closedReason(state: ReportState, fallback: string): string {
  if (state.status === 'forbidden' && state.message && /нельзя ограничить|не дел(я|и)тся по/.test(state.message)) {
    return 'Не делится по городам, а вам открыта только часть городов'
  }
  return fallback
}

export function Kpi({
  label,
  value,
  format,
  memoryKey,
  delta,
  hint,
  tag,
  spark,
  sparkColor,
  sparkGhost,
  tone,
  state,
  closedText,
  closedLabel,
  onOpen,
  index = 0,
  retry,
}: {
  label: string
  value: number | null
  format: (value: number) => string
  memoryKey: string
  delta?: ReactNode
  hint?: ReactNode
  /** Оговорка рядом с подписью: «вся компания». */
  tag?: string
  spark?: Array<number | null>
  sparkColor?: string
  /** Индекс прошлого периода на мини-тренде (полый маркер). */
  sparkGhost?: number
  tone?: 'bad'
  state: 'loading' | 'ready' | 'forbidden' | 'error' | 'idle'
  closedText?: string
  /** Заголовок закрытой карточки, если причина не права: «Не посчитать». */
  closedLabel?: string
  onOpen?: () => void
  index?: number
  /** Повторить упавший запрос — кнопка в карточке с ошибкой. */
  retry?: () => void
}) {
  const style = { '--i': index } as CSSProperties

  if (state === 'loading') return <KpiSkeleton index={index} />

  if (state === 'forbidden' || state === 'error' || state === 'idle') {
    return (
      <div className="kpi kpi--closed cascade" style={style}>
        <div className="kpi__label">{label}</div>
        <div className="kpi__closed">{state === 'error' ? 'Не удалось получить' : (closedLabel ?? 'Закрыто правами')}</div>
        <div className="kpi__hint">{state === 'error' ? 'Сервер не ответил' : (closedText ?? 'Доступ выдаёт администратор')}</div>
        {state === 'error' && retry && (
          <button type="button" className="link-button kpi__retry" onClick={retry}>
            Повторить
          </button>
        )}
      </div>
    )
  }

  // Значение и мини-тренд — одна строка, изменение и подсказка — по строке под ними:
  // высота карточки не зависит от длины подписи, ряд карточек не прыгает.
  const body = (
    <>
      <div className="kpi__top">
        <span className="kpi__label">{label}</span>
        {tag && (
          <span className="kpi__tag" title={tag === 'вся компания' ? 'В 1С не привязано к городу: показано по всей компании, фильтр города не применяется' : undefined}>
            {tag}
          </span>
        )}
      </div>
      <div className="kpi__main">
        <div className={`kpi__value mono${tone === 'bad' ? ' kpi__value--bad' : ''}`}>
          <TweenText value={value} format={format} memoryKey={memoryKey} />
        </div>
        {spark && spark.length > 1 && <Sparkline values={spark} color={sparkColor} label={`${label}: 12 месяцев`} ghostIndex={sparkGhost} />}
      </div>
      <div className="kpi__foot">
        <div className="kpi__meta">
          <div className="kpi__line">{delta}</div>
          {hint && <div className="kpi__hint">{hint}</div>}
        </div>
      </div>
    </>
  )

  return onOpen ? (
    <button type="button" className="kpi kpi--link cascade" style={style} onClick={onOpen}>
      {body}
    </button>
  ) : (
    <div className="kpi cascade" style={style}>
      {body}
    </div>
  )
}

// --- Блок с состояниями -----------------------------------------------------

export interface EmptySpec {
  when: boolean
  title: string
  hint?: ReactNode
  /** Значок пустого состояния: по умолчанию «пусто», для «долгов нет» — галочка. */
  icon?: IconName
  /** Что сделать: «Показать июнь 2026». */
  action?: { label: string; onClick: () => void }
}

export function Panel({
  title,
  subtitle,
  actions,
  states,
  skeleton,
  empty,
  children,
  className,
  index = 0,
  fill = false,
}: {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  /** Ответы, от которых зависит блок. Блок готов, когда готовы все. */
  states: ReportState[]
  skeleton?: ReactNode
  /** Если задано и истинно — пусто: причина и что делать. */
  empty?: EmptySpec
  children: () => ReactNode
  className?: string
  index?: number
  /** Последний блок колонки обзора: тянется до низа соседней колонки. */
  fill?: boolean
}) {
  const forbidden = states.find((state) => state.status === 'forbidden')
  const failed = states.find((state) => state.status === 'error')
  const idle = states.length > 0 && states.every((state) => state.status === 'idle')
  const loading = states.some((state) => state.status === 'loading')
  const refreshing = states.some((state) => state.refreshing)
  const dim = useDelayedFlag(refreshing)

  let content: ReactNode
  if (forbidden || idle) {
    content = (
      <div className="panel__state">
        <Icon name="lock" size={22} className="panel__state-icon" />
        <div className="strong secondary">Блок закрыт вашими правами</div>
        <div className="small muted">{forbidden?.message ?? 'Отчёт не входит в ваши права доступа. Остальные блоки работают.'}</div>
      </div>
    )
  } else if (failed) {
    const retries = states.filter((state) => state.status === 'error' && state.retry)
    content = (
      <div className="panel__state" role="alert">
        <Icon name="alert" size={22} className="panel__state-icon" />
        <div className="strong secondary">Не удалось получить данные</div>
        {failed.message && failed.message !== 'Не удалось получить данные' && <div className="small muted">{failed.message}</div>}
        {retries.length > 0 && (
          <button type="button" className="btn btn--sm panel__state-action" onClick={() => retries.forEach((state) => state.retry!())}>
            Повторить
          </button>
        )}
      </div>
    )
  } else if (loading) {
    content = skeleton ?? <SkeletonRows />
  } else if (empty?.when) {
    content = (
      <div className="panel__state">
        <Icon name={empty.icon ?? 'empty'} size={22} className="panel__state-icon" />
        <div className="strong secondary">{empty.title}</div>
        {empty.hint && <div className="small muted panel__state-hint">{empty.hint}</div>}
        {empty.action && (
          <button type="button" className="btn btn--sm panel__state-action" onClick={empty.action.onClick}>
            {empty.action.label}
          </button>
        )}
      </div>
    )
  } else {
    content = <div className="panel__content">{children()}</div>
  }

  return (
    <section
      className={`panel cascade${fill ? ' panel--fill' : ''}${dim ? ' panel--refreshing' : ''}${className ? ` ${className}` : ''}`}
      style={{ '--i': index } as CSSProperties}
      aria-busy={loading || refreshing}
    >
      <header className="panel__header">
        <div className="panel__heading">
          <h3 className="panel__title">{title}</h3>
          {subtitle && <div className="panel__subtitle">{subtitle}</div>}
        </div>
        {actions && <div className="panel__actions">{actions}</div>}
      </header>
      <div className="panel__body">{content}</div>
      <span className="panel__progress" aria-hidden="true" />
    </section>
  )
}

export function SkeletonRows({ rows = 5, height = 22 }: { rows?: number; height?: number }) {
  return (
    <div className="stack" style={{ gap: 12 }} aria-busy="true" aria-label="Загрузка">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="row" style={{ gap: 12, flexWrap: 'nowrap' }}>
          <div className="skeleton" style={{ height: 12, width: 120, flexShrink: 0 }} />
          <div className="skeleton" style={{ height, width: `${80 - index * 11}%` }} />
        </div>
      ))}
    </div>
  )
}

/** Скелетон полос: строки той же сетки и высоты, что у BarRows. */
export function SkeletonBars({ rows = 5, series = 2 }: { rows?: number; series?: number }) {
  return (
    <div className={`bars${series === 1 ? ' bars--single' : ''}`} style={{ '--value-width': '92px', '--aside-width': '0px' } as CSSProperties} aria-busy="true" aria-label="Загрузка">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="bars__row">
          <span className="bars__label">
            <span className="bars__name"><span className="skeleton skeleton--text" style={{ width: `${70 - index * 6}%` }} /></span>
            <span className="bars__sub"><span className="skeleton skeleton--text" style={{ width: '40%' }} /></span>
          </span>
          <span className="bars__track">
            {Array.from({ length: series }, (_, item) => (
              <span key={item} className="skeleton bars__bar" style={{ width: `${(90 - index * 14) * (item ? 0.35 : 1)}%`, transform: 'none' }} />
            ))}
          </span>
          <span className="bars__values">
            {Array.from({ length: series }, (_, item) => (
              <span key={item} className="bars__value"><span className="skeleton skeleton--text" style={{ width: 44 }} /></span>
            ))}
          </span>
          <span className="bars__aside" />
        </div>
      ))}
    </div>
  )
}

/** Скелетон сигналов: карточки той же высоты, что у готовых (значок, заголовок, текст, правило). */
export function SkeletonSignals({ count = 3 }: { count?: number }) {
  return (
    <ul className="signals" aria-busy="true" aria-label="Загрузка">
      {Array.from({ length: count }, (_, index) => (
        <li key={index}>
          <div className="signal signal--skeleton">
            <span className="signal__badge"><span className="skeleton skeleton--text" style={{ width: 64 }} /></span>
            <span className="signal__title"><span className="skeleton skeleton--text" style={{ width: '72%' }} /></span>
            <span className="signal__text"><span className="skeleton skeleton--text" style={{ width: '92%' }} /></span>
            <span className="signal__text"><span className="skeleton skeleton--text" style={{ width: '40%' }} /></span>
            <span className="signal__rule"><span className="skeleton skeleton--text" style={{ width: '84%' }} /></span>
            <span className="signal__rule"><span className="skeleton skeleton--text" style={{ width: '50%' }} /></span>
          </div>
        </li>
      ))}
    </ul>
  )
}

export function SkeletonColumns({ height = 200 }: { height?: number }) {
  return (
    <div aria-busy="true" aria-label="Загрузка">
      <div className="skeleton" style={{ height: 14, width: 220, marginBottom: 14 }} />
      <div className="skeleton-columns" style={{ height }}>
        {Array.from({ length: 12 }, (_, index) => (
          <div key={index} className="skeleton" style={{ height: `${35 + ((index * 37) % 55)}%` }} />
        ))}
      </div>
    </div>
  )
}

// --- Сегментированный переключатель ----------------------------------------

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  size,
}: {
  options: Array<{ value: T; label: string; disabled?: boolean }>
  value: T
  onChange: (value: T) => void
  label: string
  size?: 'sm'
}) {
  const index = Math.max(0, options.findIndex((option) => option.value === value))

  // Сегменты одной ширины: бегунок едет transform'ом на целое число своих
  // ширин, без измерений и без анимации width.
  return (
    <div
      className={`segmented${size ? ` segmented--${size}` : ''}`}
      role="radiogroup"
      aria-label={label}
      style={{ '--count': options.length } as CSSProperties}
    >
      <span className="segmented__thumb" style={{ transform: `translateX(${index * 100}%)` }} aria-hidden="true" />
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          disabled={option.disabled}
          className={`segmented__option${option.value === value ? ' segmented__option--on' : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

// --- Сигналы ----------------------------------------------------------------

export interface Signal {
  key: string
  tone: 'critical' | 'warning' | 'positive' | 'info'
  title: string
  text: ReactNode
  rule: string
  onOpen: () => void
}

const TONE_LABEL: Record<Signal['tone'], string> = {
  critical: 'Критично',
  warning: 'Внимание',
  positive: 'Хорошо',
  info: 'К сведению',
}

const TONE_ICON: Record<Signal['tone'], IconName> = {
  critical: 'alert',
  warning: 'tri-down',
  positive: 'tri-up',
  info: 'info',
}

/** Видно сразу три сигнала, остальные — по кнопке: блок не растягивает сетку. */
const SIGNALS_SHOWN = 3

export function SignalList({ signals, limit = SIGNALS_SHOWN }: { signals: Signal[]; limit?: number }) {
  const [all, setAll] = useState(false)
  const SIGNALS_SHOWN = limit
  const shown = all ? signals : signals.slice(0, SIGNALS_SHOWN)
  const rest = signals.length - shown.length
  return (
    <>
      <ul className="signals">
        {shown.map((signal, index) => (
          <li key={signal.key} className="cascade" style={{ '--i': Math.min(index, 7) } as CSSProperties}>
            <button type="button" className={`signal signal--${signal.tone}`} onClick={signal.onOpen}>
              <span className="signal__badge">
                <Icon name={TONE_ICON[signal.tone]} size={12} /> {TONE_LABEL[signal.tone]}
              </span>
              <span className="signal__title">{signal.title}</span>
              <span className="signal__text">{signal.text}</span>
              <span className="signal__rule">{signal.rule}</span>
              <Icon name="arrow-right" className="signal__arrow" />
            </button>
          </li>
        ))}
      </ul>
      {(rest > 0 || all) && signals.length > SIGNALS_SHOWN && (
        <button type="button" className="link-button signals__more" onClick={() => setAll((value) => !value)} aria-expanded={all}>
          {all ? 'Свернуть' : `Ещё ${rest} ${rest === 1 ? 'сигнал' : rest < 5 ? 'сигнала' : 'сигналов'}`}
        </button>
      )}
    </>
  )
}

// --- Оговорки ---------------------------------------------------------------

/** Оговорка финансового блока — та же компонента, что на всех экранах. */
export function Caveat({ children, tone = 'info' }: { children: ReactNode; tone?: 'info' | 'warn' | 'bad' }) {
  return <Notice tone={tone}>{children}</Notice>
}

/** Предупреждения сервера — дословно и без повторов. */
export function DataNotes({ states }: { states: ReportState[] }) {
  const warnings = [
    ...new Set(
      states
        .flatMap((state) => {
          const meta = state.data?.meta
          // comparison_note — тот же текст, что в warnings: показываем один раз.
          return meta ? [...(meta.comparison_note ? [meta.comparison_note] : []), ...meta.warnings] : []
        })
        .filter((text) => !text.startsWith('Выдано ')),
    ),
  ]
  const narrowed = states.find((state) => state.data?.meta.period_applied.narrowed)?.data?.meta.period_applied
  if (warnings.length === 0 && !narrowed) return null

  return (
    <details className="caveat caveat--details">
      <summary>
        <Icon name="info" className="caveat__icon" />
        <span className="caveat__count">{warnings.length + (narrowed ? 1 : 0)}</span> Примечания к данным
        <Icon name="chevron-down" />
      </summary>
      <ul>
        {narrowed && (
          <li>
            Период сужен вашими правами: данные с {formatDate(narrowed.from)} по {formatDate(narrowed.to)}.
          </li>
        )}
        {warnings.map((text) => (
          <li key={text}>{text}</li>
        ))}
      </ul>
    </details>
  )
}

/**
 * Свежесть выгрузки и, если она отстаёт по документам, последний месяц
 * с продажами. «Данные 2 минуты назад» при продажах по июнь — правда
 * наполовину: выгрузка свежая, а документов за лето в ней нет.
 */
export function Freshness({ asOf, lastSales = null, failed = false }: { asOf: string | null; lastSales?: string | null; failed?: boolean }) {
  if (failed) {
    return (
      <span className="freshness freshness--failed" title={asOf ? `Последний полученный ответ — данные в 1С на ${formatDateTime(asOf)}` : undefined}>
        <span className="freshness__dot" aria-hidden="true" />
        Нет ответа сервера{asOf ? ` · на экране данные ${formatRelative(asOf)}` : ''}
      </span>
    )
  }
  if (!asOf && !lastSales) return null
  const hours = asOf ? (Date.now() - new Date(asOf).getTime()) / 3_600_000 : 0
  const stale = hours > 48 || Boolean(lastSales)
  return (
    <span
      className={`freshness${stale ? ' freshness--stale' : ''}`}
      title={asOf ? `Данные в 1С на ${formatDateTime(asOf)}${lastSales ? `; последние продажи — ${lastSales}` : ''}` : undefined}
    >
      <span className="freshness__dot" aria-hidden="true" />
      {asOf && (hours > 48 ? 'Данные устарели: ' : 'Данные ')}
      {asOf && formatRelative(asOf)}
      {lastSales && <span className="freshness__last">{asOf ? ' · ' : ''}продажи по {lastSales}</span>}
    </span>
  )
}
